import ChevronDown from '@assets/icons/chevron-down.svg';
import ChevronUp from '@assets/icons/chevron-up.svg';
import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import validator from 'validator';
import { AccountPickerList } from './account-picker-list';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { EmailInput } from '@/components/email-input';
import { EmployeeRestaurant } from '@/contexts/user-context';
import {
  formatPhoneNumber,
  isNotMaliPhoneNumber,
  isValidPhoneNumber,
} from '@/utils/phone-number';
import { KeyboardAwareScrollView } from '@/components/with-keyboard-aware';
import { ListItem } from '@usepepper/ui/molecules/list-item';
import {
  MatchingAccountActionSheet,
  SelectedMatchingAccountType,
} from '@/screens/restaurant/matching-account-action-sheet';
import { PhoneNumberInput } from '@/components/phone-number-input';
import {
  Platform,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { RestaurantEmployeeFragment } from '@/types';
import { SCREEN_PADDING, SCREEN_PADDING_SMALL } from '@/styles';
import { SelectEmployeeList } from './select-employee-list';
import { SelectInput } from '@/components/select-input';
import { Switch } from '@/components/switch';
import { TextInput } from '@/components/text-input';
import { Toggle } from '@usepepper/ui/atoms/toggle';
import { Typography } from '@usepepper/ui/atoms/typography';
import { UnknownContactInfo } from './types';
import { useEmployeeInvite } from '@/hooks/use-employee-invite';
import { useKeyboardVisibility } from '@/hooks/use-keyboard-visibility';
import { useLayoutContext } from '@/contexts/layout-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

enum ContactType {
  EXISTING = 'EXISTING',
  NEW = 'NEW',
}

interface Props {
  createdAt: string;
  initialActiveRestaurant: EmployeeRestaurant | null;
  isBottomSheetExpanded: boolean;
  onUnknownContactSave: (contactSaveData: {
    contactInfo?: RestaurantEmployeeFragment | null;
    employeeUUID?: string;
    overrideAssigningRestaurant: EmployeeRestaurant;
    skipLoadingState?: boolean;
  }) => Promise<void>;
  paddingHorizontal?: number;
  unknownContactInfo: UnknownContactInfo;
}

export const InboundUnknownContact: React.FC<Props> = ({
  createdAt,
  paddingHorizontal,
  initialActiveRestaurant,
  unknownContactInfo,
  onUnknownContactSave,
  isBottomSheetExpanded,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showSelectionActionSheet, setShowSelectionActionSheet] =
    useState(false);
  const [selectedRestaurant, setSelectedRestaurant] =
    useState<EmployeeRestaurant | null>(initialActiveRestaurant);
  const skipLoadingState = initialActiveRestaurant === selectedRestaurant;
  const { emailAddress: initialEmailAddress, phoneNumber: initialPhoneNumber } =
    unknownContactInfo;
  const [selectedContactType, setSelectedContactType] = useState(
    ContactType.NEW,
  );
  const isNewContact = selectedContactType === ContactType.NEW;
  const [employeeName, setEmployeeName] = useState('');
  const [emailAddress, setEmailAddress] = useState(initialEmailAddress ?? '');
  const [phoneNumber, setPhoneNumber] = useState<string | null>(
    initialPhoneNumber ?? null,
  );
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [selectedEmployee, setSelectedEmployee] =
    useState<RestaurantEmployeeFragment | null>(null);

  const { handleInviteEmployee } = useEmployeeInvite({
    contactEmail: emailAddress,
    disableDefaultNotifications: true,
    hasInviteConflictError: () => setShowMatchingAccountActionSheet(true),
    isAdmin,
    loginEmail: emailAddress,
    name: employeeName,
    onComplete: (employeeUUID) => {
      const restaurant = selectedRestaurant ?? initialActiveRestaurant;
      if (!restaurant) {
        return;
      }
      onUnknownContactSave({
        employeeUUID,
        overrideAssigningRestaurant: restaurant,
        skipLoadingState,
      });
    },
    restaurantName: selectedRestaurant?.name ?? '',
    restaurantRelationship: selectedRestaurant ?? undefined,
    restaurantUUID: selectedRestaurant?.uuid ?? '',
    validatedPhoneNumber: phoneNumber,
  });

  const [showMatchingAccountActionSheet, setShowMatchingAccountActionSheet] =
    useState(false);
  const handleMatchingAccountActionSheetClose = async (
    selectedValue?: SelectedMatchingAccountType,
  ) => {
    setShowMatchingAccountActionSheet(false);
    if (selectedValue) {
      await handleInviteEmployee(selectedValue);
    }
  };

  const { top, bottom } = useSafeAreaInsets();
  const { height } = useWindowDimensions();
  const marginTop = height * 0.08 + top; // Top margin is 8% of screen height + the safe inset
  const modalHeight = height * 0.85 - 52;
  const { isSmallLayout } = useLayoutContext();
  const isKeyboardVisible = useKeyboardVisibility();
  const buttonBottomPadding = Math.max(SCREEN_PADDING_SMALL, bottom);
  const [buttonContainerHeight, setButtonContainerHeight] = useState<
    number | null
  >(null);

  const [styles, theme] = useStyles();

  const AdvancedOptionsIcon = showAdvancedOptions ? ChevronUp : ChevronDown;

  const parsedPhoneNumber = initialPhoneNumber
    ? formatPhoneNumber(initialPhoneNumber)
    : null;

  const [isEmailFocused, setIsEmailFocused] = useState(false);
  const [isPhoneNumberFocused, setIsPhoneNumberFocused] = useState(false);

  const isPrimaryContactEmail = Boolean(initialEmailAddress);
  const isEmailValid = validator.isEmail(emailAddress);
  const isPhoneNumberValid = isValidPhoneNumber(phoneNumber);
  const isOptionalEmailValid = isPrimaryContactEmail ? isEmailValid : true;
  const isOptionalPhoneNumberValid = !isPrimaryContactEmail
    ? isPhoneNumberValid
    : true;
  const isSaveButtonDisabled = isNewContact
    ? !employeeName ||
      !isOptionalEmailValid ||
      !isOptionalPhoneNumberValid ||
      Boolean(emailAddress && !isEmailValid) ||
      Boolean(phoneNumber && !isPhoneNumberValid)
    : !selectedEmployee;

  const emailInputProps = {
    containerStyle: styles.inputContainer,
    error:
      !isEmailValid && !isEmailFocused ? i18n.t('toast.invalid_email') : null,
    inDraggableSheet: isSmallLayout,
    label: i18n.t('email_address'),
    onBlur: () => setIsEmailFocused(false),
    onChangeText: setEmailAddress,
    onFocus: () => {
      setIsEmailFocused(true);
    },
    placeholder: i18n.t('email_address'),
    value: emailAddress,
  };

  const phoneInputProps = {
    allowInvalidNumber: true,
    containerStyle: styles.inputContainer,
    error:
      !isPhoneNumberValid && !isPhoneNumberFocused
        ? i18n.t('toast.invalid_phone_number')
        : null,
    inDraggableSheet: isSmallLayout,
    initialValue: phoneNumber ?? initialPhoneNumber,
    onBlur: () => setIsPhoneNumberFocused(false),
    onChangeText: setPhoneNumber,
    onFocus: () => {
      setIsPhoneNumberFocused(true);
    },
  };

  // KeyboardAwareScrollView assumes the scroll view is at the bottom of the screen
  // which may not happen on tablets. Compensate by calculating the height
  // of the content below the scroll view and subtracting it from the offset
  const keyboardExtraSpacing = buttonContainerHeight
    ? -buttonContainerHeight
    : 0;

  return (
    <>
      <KeyboardAwareScrollView
        autoAdjustDisabled={!isBottomSheetExpanded}
        contentContainerStyle={{
          paddingHorizontal,
        }}
        extraSpacing={keyboardExtraSpacing}
        keyboardDismissMode={
          isSmallLayout && Platform.OS !== 'web' ? 'on-drag' : undefined
        }
        style={styles.scrollView}
      >
        <SelectInput
          inputContainerStyle={styles.selectInput}
          label=""
          onPress={() => setShowSelectionActionSheet(true)}
          placeholder={i18n.t('select_account')}
          placeholderTextColor={theme.colors.primary}
          rightElement={
            <ChevronDown fill={theme.colors.primary} height={8} width={14} />
          }
          testID="unknown-contact-select-account-input"
          value={selectedRestaurant?.name ?? ''}
        />
        <Typography variant="label-md">
          <Text style={{ color: theme.sk.color.text.error }}>
            {i18n.t('add_missing_employee')}
          </Text>
          <Text> · {initialEmailAddress ?? parsedPhoneNumber}</Text>
        </Typography>
        <Typography variant="paragraph-md">{createdAt}</Typography>
        {selectedRestaurant ? (
          <>
            <View style={styles.toggleContainer}>
              <Toggle
                onPress={setSelectedContactType}
                selectedTab={selectedContactType}
                tabs={[
                  { label: i18n.t('new'), value: ContactType.NEW },
                  { label: i18n.t('existing'), value: ContactType.EXISTING },
                ]}
              />
            </View>
            {isNewContact ? (
              <>
                <View style={styles.formFieldWrapper}>
                  <TextInput
                    autoFocus={Platform.OS === 'web' && !isSmallLayout}
                    containerStyle={styles.inputContainer}
                    inDraggableSheet={isSmallLayout}
                    label={employeeName ? i18n.t('employee_name') : ''}
                    onChangeText={setEmployeeName}
                    placeholder={`${i18n.t('employee_name')}*`}
                    testID="unknown-contact-employee-name-input"
                    value={employeeName}
                  />
                </View>
                <View style={styles.formFieldWrapper}>
                  {isPrimaryContactEmail ? (
                    <EmailInput
                      {...emailInputProps}
                      testID="unknown-contact-email-address-input"
                    />
                  ) : (
                    <PhoneNumberInput
                      {...phoneInputProps}
                      testID="unknown-contact-phone-number-input"
                    />
                  )}
                </View>
                <TouchableOpacity
                  hitSlop={16}
                  onPress={() => {
                    setShowAdvancedOptions((prev) => !prev);
                  }}
                  style={[
                    styles.advancedOptionsContainer,
                    showAdvancedOptions
                      ? styles.advancedOptionsContainerExpanded
                      : null,
                  ]}
                >
                  <Typography
                    color={theme.colors.primary}
                    variant="paragraph-md"
                  >
                    {i18n.t('advanced_options')}
                  </Typography>
                  <AdvancedOptionsIcon
                    fill={theme.colors.primary}
                    height={4}
                    width={8}
                  />
                </TouchableOpacity>
                {showAdvancedOptions ? (
                  <>
                    <View style={styles.formFieldWrapper}>
                      {isPrimaryContactEmail ? (
                        <PhoneNumberInput
                          {...phoneInputProps}
                          error={
                            Boolean(phoneNumber) &&
                            !isPhoneNumberValid &&
                            !isPhoneNumberFocused
                              ? i18n.t('toast.invalid_phone_number')
                              : null
                          }
                          testID="unknown-contact-optional-phone-number-input"
                        />
                      ) : (
                        <EmailInput
                          {...emailInputProps}
                          error={
                            emailAddress && !isEmailValid && !isEmailFocused
                              ? i18n.t('toast.invalid_email')
                              : null
                          }
                          testID="unknown-contact-optional-email-address-input"
                        />
                      )}
                    </View>
                    <ListItem
                      rightElement={
                        <Switch
                          onValueChange={setIsAdmin}
                          testID="unknown-contact-admin-access-switch"
                          trackingData={{
                            name: 'unknown_contact_admin_access',
                            properties: {
                              isAdmin: !isAdmin,
                            },
                          }}
                          value={isAdmin}
                        />
                      }
                      subtitle={i18n.t('admin_access.description')}
                      title={i18n.t('admin_access.title')}
                    />
                  </>
                ) : null}
              </>
            ) : (
              <SelectEmployeeList
                contactInfo={unknownContactInfo}
                employeeItemStyle={styles.employeeItem}
                hasItemDivider={false}
                hideAddNew
                hideUpdateEmployees
                isEmployeeSelectable={(employee) => {
                  if (!employee) {
                    return false;
                  }
                  const arePhoneNumbersEqual =
                    employee.employee_contact_phone_number ===
                    initialPhoneNumber;
                  const areEmailsEqual =
                    employee.employee_login_email === emailAddress;
                  const isValidPhoneNumber =
                    employee.employee_contact_phone_number
                      ? isNotMaliPhoneNumber(
                          employee.employee_contact_phone_number,
                        )
                      : null;
                  const hasPhoneConflict =
                    initialPhoneNumber &&
                    isValidPhoneNumber &&
                    !arePhoneNumbersEqual;
                  const hasEmailConflict =
                    emailAddress &&
                    employee.employee_login_email &&
                    !areEmailsEqual;
                  return !hasPhoneConflict && !hasEmailConflict;
                }}
                onSelect={setSelectedEmployee}
                restaurantRelationship={selectedRestaurant}
                scrollEnabled={false}
                selectedEmployeeItemStyle={styles.selectedEmployee}
                selectedRestaurantEmployeeUUID={
                  selectedEmployee?.restaurant_employee_id
                }
                style={styles.employeeList}
              />
            )}
          </>
        ) : null}
      </KeyboardAwareScrollView>
      {isKeyboardVisible && isSmallLayout ? null : (
        <View
          onLayout={(e) => {
            setButtonContainerHeight(e.nativeEvent.layout.height);
          }}
          style={[
            styles.buttonContainer,
            { paddingBottom: buttonBottomPadding, paddingHorizontal },
          ]}
        >
          <Button
            disabled={isSaveButtonDisabled}
            loading={isLoading}
            onPress={async () => {
              if (!selectedRestaurant) {
                return;
              }
              setIsLoading(true);
              try {
                if (selectedEmployee) {
                  const contactInfo = { ...selectedEmployee };
                  if (contactInfo) {
                    if (isPrimaryContactEmail) {
                      if (isEmailValid) {
                        contactInfo.employee_contact_email = emailAddress;
                        contactInfo.employee_login_email = emailAddress;
                      }
                      if (isOptionalPhoneNumberValid && phoneNumber) {
                        contactInfo.employee_contact_phone_number = phoneNumber;
                      }
                    } else {
                      if (isPhoneNumberValid && phoneNumber) {
                        contactInfo.employee_contact_phone_number = phoneNumber;
                      }
                      if (isOptionalEmailValid && emailAddress) {
                        contactInfo.employee_contact_email = emailAddress;
                        contactInfo.employee_login_email = emailAddress;
                      }
                    }
                  }
                  await onUnknownContactSave({
                    contactInfo,
                    employeeUUID: selectedEmployee.employee_id,
                    overrideAssigningRestaurant: selectedRestaurant,
                    skipLoadingState,
                  });
                } else {
                  await handleInviteEmployee();
                }
              } finally {
                setIsLoading(false);
              }
            }}
            trackingData={{
              name: 'unknown_contact_save_button',
            }}
          >
            {i18n.t('save')}
          </Button>
        </View>
      )}
      <BaseActionSheet
        contentStyle={[
          styles.modalContent,
          isSmallLayout
            ? [styles.modalContentSmall, { marginTop }]
            : { height: modalHeight },
        ]}
        isVisible={showSelectionActionSheet}
        modalHeaderStyle={styles.modalHeader}
        modalStyle={styles.baseModalStyle}
        onClose={() => setShowSelectionActionSheet(false)}
        title={i18n.t('accounts')}
      >
        <AccountPickerList
          activeRestaurantUUID={selectedRestaurant?.uuid}
          onSelect={(selection) => {
            setSelectedEmployee(null);
            setSelectedRestaurant(selection);
            setShowSelectionActionSheet(false);
          }}
        />
      </BaseActionSheet>
      {showMatchingAccountActionSheet ? (
        <MatchingAccountActionSheet
          contactInfo={{
            loginEmail: emailAddress,
            phoneNumber: formatPhoneNumber(phoneNumber!),
          }}
          onClose={handleMatchingAccountActionSheetClose}
        />
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  advancedOptionsContainer: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  advancedOptionsContainerExpanded: {
    paddingBottom: 16,
  },
  baseModalStyle: {
    maxWidth: 480,
  },
  buttonContainer: {
    paddingTop: SCREEN_PADDING,
  },
  employeeItem: {
    marginHorizontal: 0,
  },
  employeeList: {
    paddingBottom: 0,
  },
  formFieldWrapper: {
    marginBottom: 16,
  },
  inputContainer: {
    borderColor: theme.sk.color.border.input.default,
  },
  modalContent: {
    paddingBottom: 0,
    paddingHorizontal: 0,
    paddingTop: SCREEN_PADDING_SMALL,
  },
  modalContentSmall: {
    flex: 1,
  },
  modalHeader: {
    paddingBottom: 0,
  },
  scrollView: {
    flexGrow: 0,
    flexShrink: 1,
  },
  selectedEmployee: {
    backgroundColor: theme.sk.color.bg.primary.active,
    borderRadius: 8,
  },
  selectInput: {
    borderColor: theme.colors.divider,
    borderRadius: 8,
    borderStyle: 'solid',
    borderWidth: 1,
    marginBottom: SCREEN_PADDING,
  },
  toggleContainer: {
    marginVertical: 16,
    maxWidth: 324,
  },
}));
